import React, { useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuthContext';
import { UsersIcon, BriefcaseIcon, ChatBubbleLeftRightIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { UsersIcon as UsersSolidIcon, BriefcaseIcon as BriefcaseSolidIcon, ChatBubbleLeftRightIcon as ChatBubbleLeftRightSolidIcon, UserCircleIcon as UserCircleSolidIcon } from '@heroicons/react/24/solid';
import NotificationBadge from '../common/NotificationBadge';
import NotificationErrorBoundary from '../common/NotificationErrorBoundary';
import { useNotifications } from '../../hooks/useNotifications';
import ProfileCompletionBadge from '../common/ProfileCompletionBadge';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuthContext();
  const { counts, clearNotificationCount, refreshCountsDebounced, markPageVisited } = useNotifications();
  const { completionPercentage } = useProfileCompletion();





  // Memoize navigation items to prevent unnecessary re-renders
  const navigation = useMemo(() => [
    {
      name: 'Members',
      href: '/network',
      icon: UsersIcon,
      iconSolid: UsersSolidIcon,
      current: location.pathname === '/network',
      notificationCount: counts.people
    },
    {
      name: 'Jobs',
      href: '/jobs',
      icon: BriefcaseIcon,
      iconSolid: BriefcaseSolidIcon,
      current: location.pathname === '/jobs',
      notificationCount: counts.opportunities
    },
    {
      name: 'Forum',
      href: '/forum',
      icon: ChatBubbleLeftRightIcon,
      iconSolid: ChatBubbleLeftRightSolidIcon,
      current: location.pathname.startsWith('/forum'),
      notificationCount: counts.forum
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: UserCircleIcon,
      iconSolid: UserCircleSolidIcon,
      current: location.pathname === '/profile',
      isProfile: true
    }
  ], [location.pathname, counts.people, counts.opportunities, counts.forum]);

  // Memoize navigation handler to prevent unnecessary re-renders
  const handleNavigation = useCallback((href, item) => {
    // Handle notification clearing first
    if (item && !item.isProfile) {
      const pageTypeMap = {
        'Members': 'people',
        'Jobs': 'opportunities', 
        'Forum': 'forum'
      };
      
      const pageType = pageTypeMap[item.name];
      if (pageType) {
        // Clear notification count immediately for better UX
        if (item.notificationCount > 0) {
          clearNotificationCount(pageType);
          
          // Mark page as visited in database in background (non-blocking)
          markPageVisited(pageType).catch(error => {
            console.warn(`Failed to mark ${pageType} as visited on navigation:`, error);
          });
        }
      }
    }
    
    // Navigate using React Router
    navigate(href, { replace: true });
    
    // Refresh notification counts after navigation with reduced delay and non-blocking
    if (!item.isProfile) {
      // Use requestIdleCallback to avoid blocking navigation
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          refreshCountsDebounced(100); // Reduced delay
        });
      } else {
        refreshCountsDebounced(200); // Fallback with shorter delay
      }
    }
  }, [navigate, location.pathname, clearNotificationCount, markPageVisited, refreshCountsDebounced]);

  return (
    <nav className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-gray-200/80 dark:bg-gray-900/95 dark:border-gray-700/80 z-50 transition-all duration-300 shadow-lg dark:shadow-2xl mobile-nav-backdrop">
      <div className="flex justify-around items-center h-16 px-6">
        {navigation.map((item) => {


          // Regular navigation items
          const IconComponent = item.current ? item.iconSolid : item.icon;
          
          return (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.href, item)}
              className={`
                relative flex flex-col items-center justify-center
                min-w-[48px] min-h-[48px] px-4 py-2 rounded-xl
                transition-all duration-200 transform
                active:scale-95 navigation-item
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900
                ${item.current
                  ? 'text-purple-600 dark:text-purple-400 bg-purple-50/80 dark:bg-purple-900/30 shadow-md ring-1 ring-purple-200/50 dark:ring-purple-700/50'
                  : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-50/80 dark:hover:bg-gray-800/50 hover:shadow-sm'
                }
              `}
              aria-label={`Navigate to ${item.name}${item.notificationCount > 0 ? ` (${item.notificationCount} new)` : ''}`}
              aria-current={item.current ? 'page' : undefined}
              data-walkthrough-tab={item.name}
            >
              <div className="relative">
                <IconComponent className={`w-6 h-6 mb-1 transition-all duration-200 ${item.current ? 'scale-110 drop-shadow-sm' : 'group-hover:scale-105'}`} />
                <NotificationErrorBoundary>
                  {item.notificationCount > 0 && (
                    <NotificationBadge 
                      count={item.notificationCount} 
                      className="absolute -top-2 -right-2" 
                    />
                  )}
                </NotificationErrorBoundary>
                
                {/* Profile Completion Badge for Profile Tab */}
                {item.isProfile && (
                  <ProfileCompletionBadge
                    completionPercentage={completionPercentage}
                    variant="mobile"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/profile');
                    }}
                  />
                )}
              </div>
              <span className={`text-xs font-medium leading-none transition-all duration-200 ${item.current ? 'font-semibold' : ''}`}>
                {item.name}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;